// src/pages/Partner/PartnerHome.jsx



import {

  useCallback,

  useEffect,

  useMemo,

  useState,

} from "react";



import {

  AlertTriangle,

  ArrowRight,

  Download,

  Plus,

  UserRound,

  UserPlus,

  Users,

} from "lucide-react";



import {

  Link,

  useOutletContext,

} from "react-router-dom";



import EntrepreneurStatusTabs from "../../components/partner/EntrepreneurStatusTabs";



import {

  fetchPartnerInvitations,

} from "../../lib/partner/partnerInvitations";



import { supabase } from "../../supabaseClient";



import {

  fetchPartnerAttentionDataset,

} from "../../lib/partner/partnerAttention";





const ACTIVITY_WINDOW_DAYS = 7;





/* -------------------------------------------------------------------------- */

/* Helpers                                                                    */

/* -------------------------------------------------------------------------- */



function getFirstName(user) {

  const metadata =

    user?.user_metadata || {};



  const fullName =

    metadata.full_name ||

    metadata.name ||

    metadata.display_name ||

    "";



  const cleanName =

    String(fullName).trim();



  if (cleanName) {

    return (

      cleanName.split(/\s+/)[0] ||

      "User"

    );

  }



  const email =

    String(

      user?.email || ""

    ).trim();



  if (email) {

    const localPart =

      email.split("@")[0];



    if (localPart) {

      return (

        localPart

          .split(/[._-]/)[0]

          .replace(

            /^./,

            (character) =>

              character.toUpperCase()

          ) || "User"

      );

    }

  }



  return "User";

}





function nullableNumber(value) {

  if (

    value === null ||

    value === undefined ||

    String(value).trim() === ""

  ) {

    return null;

  }



  const parsed =

    Number(value);



  return Number.isFinite(parsed)

    ? parsed

    : null;

}





function hasStartedJourney(

  learner

) {

  const levelsCompleted =

    nullableNumber(

      learner?.levels_completed

    ) || 0;



  const tasksCompleted =

    nullableNumber(

      learner?.tasks_completed

    ) || 0;



  return (

    levelsCompleted > 0 ||

    tasksCompleted > 0 ||

    Boolean(

      learner?.current_quest_id ||

        learner?.current_subquest_id ||

        learner?.current_level_key

    )

  );

}





/* -------------------------------------------------------------------------- */

/* Page                                                                       */

/* -------------------------------------------------------------------------- */



export default function PartnerHome() {

  const {

    access,

  } = useOutletContext();





  const [

    userFirstName,

    setUserFirstName,

  ] = useState("User");



  const [

  entrepreneurStatusTab,

  setEntrepreneurStatusTab,

] = useState("attention");





  const [

    dataset,

    setDataset,

  ] = useState({

    cohorts: [],

    activeCohorts: [],

    progressRows: [],

    activeParticipants: [],

    attentionRows: [],

  });





  const [

    invitationRows,

    setInvitationRows,

  ] = useState([]);





  const [

    loading,

    setLoading,

  ] = useState(true);





  const [

    error,

    setError,

  ] = useState("");





  const [

    invitationsError,

    setInvitationsError,

  ] = useState("");





  /* ------------------------------------------------------------------------ */

  /* Current user                                                             */

  /* ------------------------------------------------------------------------ */



  useEffect(() => {

    let alive = true;



    async function loadCurrentUser() {

      try {

        const {

          data,

        } =

          await supabase.auth

            .getUser();



        if (!alive) {

          return;

        }



        setUserFirstName(

          getFirstName(

            data?.user

          )

        );

      } catch (

        loadError

      ) {

        console.error(

          "Could not load Partner user name:",

          loadError

        );

      }

    }



    loadCurrentUser();



    return () => {

      alive = false;

    };

  }, []);





  /* ------------------------------------------------------------------------ */

  /* Data                                                                     */

  /* ------------------------------------------------------------------------ */



  const loadOverview =

    useCallback(

      async () => {

        try {

          setLoading(true);

          setError("");

          setInvitationsError("");



          const partnerRole =

            access?.partner_role ||

            "";



          const canManageInvitations =

            [

              "owner",

              "admin",

              "cohort_manager",

            ].includes(

              partnerRole

            );



          const [

            datasetResult,

            invitationsResult,

          ] =

            await Promise.allSettled(

              [

                fetchPartnerAttentionDataset(

                  partnerRole

                ),



                canManageInvitations

                  ? fetchPartnerInvitations()

                  : Promise.resolve(

                      []

                    ),

              ]

            );



          if (

            datasetResult.status !==

            "fulfilled"

          ) {

            throw datasetResult.reason;

          }



          setDataset(

            datasetResult.value

          );



          if (

            invitationsResult.status ===

            "fulfilled"

          ) {

            setInvitationRows(

              Array.isArray(

                invitationsResult.value

              )

                ? invitationsResult.value

                : []

            );

          } else {

            console.error(

              "Partner overview invitation loading failed:",

              invitationsResult.reason

            );



            setInvitationRows(

              []

            );



            setInvitationsError(

              invitationsResult.reason

                ?.message ||

                "Pending invitations could not be loaded."

            );

          }

        } catch (

          loadError

        ) {

          console.error(

            "Partner overview loading failed:",

            loadError

          );



          setDataset({

            cohorts: [],

            activeCohorts: [],

            progressRows: [],

            activeParticipants: [],

            attentionRows: [],

          });



          setInvitationRows(

            []

          );



          setError(

            loadError?.message ||

              "The partner overview could not be loaded."

          );

        } finally {

          setLoading(false);

        }

      },

      [

        access?.partner_role,

      ]

    );





  useEffect(() => {

    loadOverview();

  }, [loadOverview]);





  /* ------------------------------------------------------------------------ */

  /* Overview metrics                                                         */

  /* ------------------------------------------------------------------------ */



  const metrics =

    useMemo(() => {

      const activeParticipants =

        dataset

          .activeParticipants;





      const activeThisWeek =

        activeParticipants.filter(

          (learner) => {

            const days =

              nullableNumber(

                learner

                  .days_since_activity

              );



            return (

              learner

                .last_activity_at &&

              days !== null &&

              days >= 0 &&

              days <

                ACTIVITY_WINDOW_DAYS

            );

          }

        );





      const activeThisWeekPercent =

        activeParticipants.length >

        0

          ? Math.round(

              (

                activeThisWeek.length /

                activeParticipants.length

              ) *

                100

            )

          : 0;





      return {

        activeCohorts:

          dataset

            .activeCohorts

            .length,



        totalParticipants:

          activeParticipants.length,



        activeThisWeekPercent,



        participantsNeedingAttention:

          dataset

            .attentionRows

            .length,

      };

    }, [dataset]);





  /* ------------------------------------------------------------------------ */

  /* Cohort summary                                                           */

  /* ------------------------------------------------------------------------ */



  const cohortSummaryRows =

    useMemo(

      () =>

        dataset.activeCohorts.map(

          (cohort) => {

            const participants =

              dataset

                .activeParticipants

                .filter(

                  (learner) =>

                    String(

                      learner

                        .cohort_id

                    ) ===

                    String(

                      cohort.id

                    )

                );





            const attentionRows =

              dataset

                .attentionRows

                .filter(

                  (learner) =>

                    String(

                      learner

                        .cohort_id

                    ) ===

                    String(

                      cohort.id

                    )

                );





            const activated =

              participants.filter(

                hasStartedJourney

              );





            const activeLast7Days =

              participants.filter(

                (learner) => {

                  const days =

                    nullableNumber(

                      learner

                        .days_since_activity

                    );



                  return (

                    learner

                      .last_activity_at &&

                    days !== null &&

                    days >= 0 &&

                    days <

                      ACTIVITY_WINDOW_DAYS

                  );

                }

              );





            const activePercent =

              participants.length >

              0

                ? Math.round(

                    (

                      activeLast7Days.length /

                      participants.length

                    ) *

                      100

                  )

                : 0;





            return {

              id:

                cohort.id,



              name:

                cohort.name ||

                "Cohort",



              participants:

                participants.length,



              activated:

                activated.length,



              activeLast7Days:

                activeLast7Days.length,



              activePercent,



              needsAttention:

                attentionRows.length,

            };

          }

        ),

      [

        dataset

          .activeCohorts,

        dataset

          .activeParticipants,

        dataset

          .attentionRows,

      ]

    );





  /* ------------------------------------------------------------------------ */

  /* Inline table navigation                                                  */

  /* ------------------------------------------------------------------------ */



  const scrollToOverviewSection =

    useCallback(

      (sectionId) => {

        const element =

          document.getElementById(

            sectionId

          );



        if (!element) {

          return;

        }



        element.scrollIntoView({

          behavior: "smooth",

          block: "start",

        });

      },

      []

    );





  /* ------------------------------------------------------------------------ */

  /* Quick actions                                                            */

  /* ------------------------------------------------------------------------ */



  const canManage =

    [

      "owner",

      "admin",

      "cohort_manager",

    ].includes(

      access?.partner_role

    );





  const quickActions =

    useMemo(

      () => [

        {

          to: "/partner/cohorts",

          label: canManage

            ? "Create cohort"

            : "View cohorts",

          icon: Plus,

        },



        {

          to: canManage

            ? "/partner/invitations"

            : "/partner/learners",

          label: canManage

            ? "Invite participants"

            : "View participants",

          icon: UserPlus,

        },



        {

          to:

            "/partner/overview-export",

          label:

            "Export overview",

          icon: Download,

        },

      ],

      [canManage]

    );





  /* ------------------------------------------------------------------------ */

  /* Render                                                                   */

  /* ------------------------------------------------------------------------ */



  return (

    <>

      <div className="w-full">



        {/* ============================================================ */}

        {/* Greeting                                                     */}

        {/* ============================================================ */}



        <section>

          <h1 className="text-[34px] font-black tracking-[-0.045em] text-black sm:text-[40px] lg:text-[44px]">

            Hi, {userFirstName}

          </h1>



          <p className="mt-1.5 text-sm font-medium text-[#8A8A8A] sm:text-[15px]">

            Here&apos;s a summary of

            how your participants are

            performing.

          </p>

        </section>





        {/* ============================================================ */}

        {/* Error                                                        */}

        {/* ============================================================ */}



        {error && (

          <div

            role="alert"

            className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"

          >

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

              <span>

                {error}

              </span>



              <button

                type="button"

                onClick={

                  loadOverview

                }

                className="self-start font-black underline sm:self-auto"

              >

                Try again

              </button>

            </div>

          </div>

        )}





        {/* ============================================================ */}

        {/* Quick actions                                                */}

        {/* ============================================================ */}



        <section className="mt-7 rounded-[14px] border border-[#D3D3D3] bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sm:p-5">

          <h2 className="text-[20px] font-black tracking-[-0.025em] text-black sm:text-[22px]">

            Quick actions

          </h2>



          <div className="mt-3 grid gap-2.5 md:grid-cols-3">

            {quickActions.map(

              ({

                to,

                label,

                icon: Icon,

              }) => (

                <Link

                  key={label}

                  to={to}

                  className="group flex min-h-[46px] items-center rounded-[10px] border border-[#D6D6D6] bg-white px-4 py-3 text-sm font-semibold text-[#1F1F1F] shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition hover:border-[#FF914D] hover:bg-[#FFF9F5]"

                >

                  <Icon className="mr-3 h-[18px] w-[18px] flex-none stroke-[1.6] text-[#FF914D]" />



                  <span>

                    {label}

                  </span>

                </Link>

              )

            )}

          </div>

        </section>





        {/* ============================================================ */}

        {/* Metrics                                                      */}

        {/* ============================================================ */}



        <section className="mt-7">

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">



            <MetricCard

              label="Active cohorts"

              value={

                loading

                  ? "..."

                  : metrics

                      .activeCohorts

              }

              icon={Users}

              actionLabel="View cohort summary"

              onAction={() =>

                scrollToOverviewSection(

                  "cohort-performance-summary"

                )

              }

            />





            <MetricCard

              label={

                <>

                  Total participants in

                  <br />

                  active cohorts

                </>

              }

              value={

                loading

                  ? "..."

                  : metrics

                      .totalParticipants

              }

              icon={UserRound}

            />





            <MetricCard

              label={

                <>

                  Active participants this

                  <br />

                  week

                </>

              }

              value={

                loading

                  ? "..."

                  : `${metrics.activeThisWeekPercent}%`

              }

              icon={Users}

            />





            <MetricCard

              label={

                <>

                  Participants needing

                  <br />

                  attention

                </>

              }

              value={

                loading

                  ? "..."

                  : metrics

                      .participantsNeedingAttention

              }

              icon={

                AlertTriangle

              }

              actionLabel="View entrepreneurs"

              onAction={() => {

  setEntrepreneurStatusTab(

    "attention"

  );



  requestAnimationFrame(

    () => {

      scrollToOverviewSection(

        "entrepreneurs-needing-attention"

      );

    }

  );

}}

            />



          </div>

        </section>





        {/* ============================================================ */}

        {/* Entrepreneur status tabs                                    */}

        {/* ============================================================ */}



        <section

          id="entrepreneurs-needing-attention"

          className="mt-7 scroll-mt-6"

        >

          <EntrepreneurStatusTabs

  title="Entrepreneurs"

  activeTab={

    entrepreneurStatusTab

  }

  onActiveTabChange={

    setEntrepreneurStatusTab

  }

            description="Review entrepreneurs who need follow-up, pending invitations and login inactivity."

            attentionRows={

              dataset.attentionRows

            }

            invitationRows={

              invitationRows

            }

            progressRows={

              dataset.activeParticipants

            }

            canViewPending={

              canManage

            }

            showCohort

            maxRows={5}

            loading={loading}

            pendingError={

              invitationsError

            }

            fullListRoutes={{

              attention:

                "/partner/attention",



              pending:

                "/partner/invitations",

            }}

          />

        </section>





        {/* ============================================================ */}

        {/* Cohort performance summary                                  */}

        {/* ============================================================ */}



        <section

          id="cohort-performance-summary"

          className="mt-7 scroll-mt-6"

        >

          <CohortPerformanceSection

            rows={

              cohortSummaryRows

            }

            loading={loading}

          />

        </section>



      </div>

    </>

  );

}





/* -------------------------------------------------------------------------- */

/* Metric card                                                                */

/* -------------------------------------------------------------------------- */



function MetricCard({

  label,

  value,

  icon: Icon,

  actionLabel = "",

  onAction,

}) {

  return (

    <article className="flex min-h-[190px] flex-col rounded-[13px] border border-[#D6D6D6] bg-white px-5 py-5 shadow-[0_1px_3px_rgba(0,0,0,0.035)]">



      <div className="flex items-start gap-3">

        <Icon className="mt-0.5 h-[19px] w-[19px] flex-none stroke-[1.35] text-[#8B8B8B]" />



        <p className="text-[14px] font-medium leading-[1.12] text-[#777777]">

          {label}

        </p>

      </div>





      <p className="mt-8 text-[36px] font-black leading-none tracking-[-0.045em] text-black sm:text-[40px]">

        {value}

      </p>





      {onAction && (

        <div className="mt-auto pt-5">

          <button

            type="button"

            onClick={

              onAction

            }

            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#FF914D] transition hover:text-[#E9782F]"

          >

            {actionLabel}



            <ArrowRight className="h-3.5 w-3.5" />

          </button>

        </div>

      )}



    </article>

  );

}





/* -------------------------------------------------------------------------- */

/* Cohort performance summary                                                 */

/* -------------------------------------------------------------------------- */



function CohortPerformanceSection({

  rows,

  loading,

}) {

  const previewRows =

    rows.slice(

      0,

      5

    );





  return (

    <div className="overflow-hidden rounded-[14px] border border-[#D3D3D3] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">



      {/* Header */}



      <div className="flex flex-col gap-3 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">



        <div>

          <h2 className="text-[20px] font-black tracking-[-0.025em] text-black sm:text-[22px]">

            Cohort performance summary

          </h2>



          <p className="mt-1 text-sm font-medium text-[#858585]">

            A snapshot of participant

            engagement across your active

            cohorts.

          </p>

        </div>





        <Link

          to="/partner/cohorts"

          className="inline-flex items-center gap-1.5 text-sm font-bold text-[#FF914D] transition hover:text-[#E9782F]"

        >

          View cohorts



          <ArrowRight className="h-4 w-4" />

        </Link>



      </div>





      {/* Content */}



      {loading ? (

        <div className="border-t border-[#D8D8D8] px-6 py-14 text-center">

          <p className="font-semibold text-[#777777]">

            Loading cohort performance...

          </p>

        </div>

      ) : previewRows.length ===

        0 ? (

        <div className="border-t border-[#D8D8D8] px-6 py-14 text-center">

          <p className="font-bold text-[#555555]">

            There are no active cohorts

            to display.

          </p>

        </div>

      ) : (

        <>

          {/* Desktop headings */}



          <div className="hidden grid-cols-[1.4fr_0.8fr_0.8fr_1.2fr_0.9fr_110px] items-center gap-4 border-y border-[#D8D8D8] bg-[#F4F4F4] px-6 py-4 text-[11px] font-black uppercase tracking-wide text-[#777777] md:grid">

            <span>

              Cohort

            </span>



            <span className="text-center">

              Participants

            </span>



            <span className="text-center">

              Activated

            </span>



            <span className="text-center leading-tight">

              Active in

              <br />

              last 7 days

            </span>



            <span className="text-center leading-tight">

              Needs

              <br />

              attention

            </span>



            <span />

          </div>





          {/* Rows */}



          <div className="divide-y divide-[#E2E2E2]">

            {previewRows.map(

              (row) => (

                <CohortPerformanceRow

                  key={row.id}

                  row={row}

                />

              )

            )}

          </div>





          {/* Footer */}



          <div className="border-t border-[#D8D8D8] px-5 py-3.5 sm:px-6">

            <p className="text-xs font-medium text-[#858585]">

              Showing up to 5 active

              cohorts.

            </p>

          </div>

        </>

      )}



    </div>

  );

}





/* -------------------------------------------------------------------------- */

/* Cohort performance row                                                     */

/* -------------------------------------------------------------------------- */



function CohortPerformanceRow({

  row,

}) {

  return (

    <>

      {/* Desktop */}



      <div className="hidden min-h-[72px] grid-cols-[1.4fr_0.8fr_0.8fr_1.2fr_0.9fr_110px] items-center gap-4 px-6 py-4 md:grid">



        <p className="truncate text-sm font-bold text-black">

          {row.name}

        </p>





        <p className="text-center text-sm font-medium text-[#333333]">

          {row.participants}

        </p>





        <p className="text-center text-sm font-medium text-[#333333]">

          {row.activated}

        </p>





        <div className="flex flex-col items-center">

          <p className="text-xs font-bold text-[#333333]">

            {row.activePercent}%

          </p>



          <div className="mt-1.5 h-[8px] w-[100px] overflow-hidden rounded-full bg-[#BCBCBC]">

            <div

              className="h-full rounded-full bg-[#FF914D]"

              style={{

                width:

                  `${Math.min(

                    100,

                    Math.max(

                      0,

                      row.activePercent

                    )

                  )}%`,

              }}

            />

          </div>

        </div>





        <p className="text-center text-sm font-medium text-[#333333]">

          {row.needsAttention}

        </p>





        <Link

          to={`/partner/cohorts/${row.id}`}

          className="inline-flex min-h-[36px] items-center justify-center rounded-full bg-black px-4 text-xs font-bold text-white transition hover:bg-[#292929]"

        >

          View cohort

        </Link>



      </div>





      {/* Mobile */}



      <article className="p-5 md:hidden">



        <div className="flex items-start justify-between gap-4">

          <div>

            <p className="font-black text-black">

              {row.name}

            </p>



            <p className="mt-1 text-xs font-medium text-[#888888]">

              {row.participants} entrepreneur

              {row.participants === 1

                ? ""

                : "s"}

            </p>

          </div>





          <Link

            to={`/partner/cohorts/${row.id}`}

            className="inline-flex min-h-[34px] flex-none items-center justify-center rounded-full bg-black px-4 text-xs font-bold text-white"

          >

            View

          </Link>

        </div>





        <div className="mt-5 grid grid-cols-2 gap-4">



          <CohortMobileMetric

            label="Activated"

            value={

              row.activated

            }

          />





          <CohortMobileMetric

            label="Needs attention"

            value={

              row.needsAttention

            }

          />





          <div className="col-span-2">

            <p className="text-[10px] font-black uppercase tracking-wide text-[#999999]">

              Active in last 7 days

            </p>



            <div className="mt-2 flex items-center gap-3">

              <div className="h-[8px] flex-1 overflow-hidden rounded-full bg-[#BCBCBC]">

                <div

                  className="h-full rounded-full bg-[#FF914D]"

                  style={{

                    width:

                      `${Math.min(

                        100,

                        Math.max(

                          0,

                          row.activePercent

                        )

                      )}%`,

                  }}

                />

              </div>



              <span className="text-sm font-black text-black">

                {row.activePercent}%

              </span>

            </div>

          </div>



        </div>

      </article>

    </>

  );

}





/* -------------------------------------------------------------------------- */

/* Mobile cohort metric                                                       */

/* -------------------------------------------------------------------------- */



function CohortMobileMetric({

  label,

  value,

}) {

  return (

    <div>

      <p className="text-[10px] font-black uppercase tracking-wide text-[#999999]">

        {label}

      </p>



      <p className="mt-1 text-xl font-black text-black">

        {value}

      </p>

    </div>

  );

}